/* eslint-disable */
import { format } from "date-fns";

export class Formatters {
  private static readonly lang = "en-US";

  // #region Numbers
  private static readonly CURRENCY_THOUSANDS_SEPARATOR: string = "’";
  private static readonly CURRENCY_DECIMAL_PLACES: number = 2;
  private static readonly CURRENCY_DECIMAL_SEPARATOR: string = ".";
  private static readonly CURRENCY_SYMBOL: string = "CHF";
  private static readonly CURRENCY_SYMBOL_POSITION: 'p' | 's' = 'p';

  private static readonly DECIMAL_THOUSANDS_SEPARATOR: string = ",";
  private static readonly DECIMAL_DECIMAL_PLACES: number = 2;
  private static readonly DECIMAL_DECIMAL_SEPARATOR: string = ".";

  private static readonly INTEGER_THOUSANDS_SEPARATOR: string = ",";

  public static formatCurrencyNumber(
    value: number,
    showSymbol: boolean = true,
    thousandSeparator: string = this.CURRENCY_THOUSANDS_SEPARATOR,
    decimalPlaces: number = this.CURRENCY_DECIMAL_PLACES,
    decimalSeparator: string = this.CURRENCY_DECIMAL_SEPARATOR,
    symbol: string = this.CURRENCY_SYMBOL,
    symbolPosition: 'p' | 's' = this.CURRENCY_SYMBOL_POSITION
  ): string {
    if (!this.isNumber(value))
      return "";

    var isCurrencySuffixed = symbolPosition === 's';
    var isNegativeNumber = Math.sign(value) === -1;
    var unsignedValue = Math.abs(value);

    var formattedAmount = Formatters.formatDecimalNumber(unsignedValue, this.lang, thousandSeparator, decimalSeparator, decimalPlaces);

    if (isNegativeNumber) {
      formattedAmount = `-${formattedAmount}`;
    }

    if (showSymbol) {
      formattedAmount = isCurrencySuffixed ? `${formattedAmount} ${symbol}` : `${symbol} ${formattedAmount}`;
    }

    return formattedAmount;
  }

  public static formatDecimalNumber(
    value: number,
    lang: string = this.lang,
    thousandSeparator: string = this.DECIMAL_THOUSANDS_SEPARATOR,
    decimalSeparator: string = this.DECIMAL_DECIMAL_SEPARATOR,
    decimalPlaces: number = this.DECIMAL_DECIMAL_PLACES
  ): string {
    if (!this.isNumber(value))
      return "";

    var stringifiedAmount = new Intl.NumberFormat(lang, {
      style: 'decimal',
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    }).format(value);

    // English US locale separates thousands with ',' and decimals with '.' => therefore they can be easily replaced
    return stringifiedAmount
      .replace(/\./g, "~~")
      .replace(/,/g, thousandSeparator)
      .replace(/~~/g, decimalSeparator);
  }

  public static formatIntegerNumber(value: number, thousandSeparator: string = this.INTEGER_THOUSANDS_SEPARATOR): string {
    if (!this.isNumber(value))
      return "";

    var roundedValue = Math.round(value);

    var stringifiedAmount = new Intl.NumberFormat(this.lang, { style: 'decimal' }).format(roundedValue);

    // English US locale separates thousands with ',' and decimals with '.' => therefore they can be easily replaced
    var formattedAmount = stringifiedAmount.replace(/,/g, thousandSeparator);

    return formattedAmount;
  }

  public static isNumber(value?: any): boolean {
    return !isNaN(parseFloat(String(value))) && (isFinite(Number(value)) || typeof value == 'number');
  }
  // #endregion Numbers

  // #region Dates
  private static readonly SHORT_DATE_FORMAT: string = 'dd.MM.yyyy';

  public static formatShortDate(value: Date, customFormat: string = this.SHORT_DATE_FORMAT): string {
    return this.formatDate(value, customFormat);
  }

  private static formatDate(value: Date, customFormat: string = this.SHORT_DATE_FORMAT) {
    return format(new Date(value), customFormat);
  }

  // #endregion Dates

  // #region Strings
  public static interpolate(template: string, ...args: any[]) {
    return template.replace(/{(\d+)}/g, function (match, number) {
      return typeof args[number] != 'undefined'
        ? args[number]
        : match;
    });
  }
  // #endregion Strings
}

declare global {
  interface Date {
    addDays: (days: number) => Date;
  }
}

Date.prototype.addDays = function (days: number): Date {
  var date = new Date(this.valueOf());
  date.setDate(date.getDate() + days);
  return date;
};

/**
 * Concatenates an array of `string`-type only args into a single `className`-style value and trims it.
 * @param args String args.
 * @returns Concatenated class name.
 */
export const cn = (...args: any[]): string => {
  return args.filter(a => typeof a === "string").join(" ").trim();
}